import {
  AlertTriangle,
  ArrowLeft,
  Check,
  CheckCircle2,
  ChevronDown,
  Download,
  ExternalLink,
  FileSearch,
  FileText,
  LoaderCircle,
  PanelsTopLeft,
  RefreshCw,
  Rows3,
  Save,
  ShieldCheck,
  Sparkles,
  Trash2,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useApi } from "../App.jsx";
import { EvidenceViewer } from "../components/EvidenceViewer.jsx";
import {
  FeedbackBanner,
  LoadingBlock,
  PageHeader,
  SectionHeader,
  ConfirmDialog,
  CopyableText,
  formatMoneyForCopy,
  useRouter,
} from "@tribunalea/ui";
import { copyableFieldMetadata } from "../lib/copyable-field.js";

const processingStatuses = new Set([
  "preparing",
  "queued",
  "extracting_text",
  "extracting_fields",
  "generating_output",
]);
const REVIEW_SPLIT_STORAGE_KEY = "instrumenta:processor:review-split";

function initialReviewSplit() {
  const stored = Number(window.localStorage.getItem(REVIEW_SPLIT_STORAGE_KEY));
  return Number.isFinite(stored) && stored >= 0.3 && stored <= 0.65
    ? stored
    : 0.43;
}

function clamp(value, minimum, maximum) {
  return Math.max(minimum, Math.min(value, maximum));
}

const statusCopy = {
  preparing: ["Guardando los documentos", "Estamos preparando los archivos. Cuando termine la carga, vas a poder volver al historial."],
  queued: ["Preparando los documentos", "El procesamiento continuará aunque vuelvas al historial o cierres esta página."],
  extracting_text: ["Leyendo los documentos", "Podés volver al historial: seguiremos identificando el texto y sus páginas."],
  extracting_fields: ["Extrayendo la información", "Podés volver al historial: seguiremos buscando los valores definidos en tu plantilla."],
  awaiting_review: ["Resultado listo para revisar", "Confirmá o corregí cada valor antes de generar el archivo."],
  generating_output: ["Generando el resultado", "Estamos completando una copia de tu plantilla."],
  completed: ["Resultado generado", "El archivo está listo para descargar."],
  failed: ["No pudimos completar el procesamiento", "Revisá el detalle e intentá nuevamente."],
};

function displayValue(field) {
  return field.correctedValue ?? field.value ?? "";
}

function correctionsForRun(run, values) {
  return (run?.fields || []).map((field) => {
    const value = values[field.key] ?? "";
    const original = field.value ?? "";
    return {
      key: field.key,
      value: value === "" ? null : value,
      status: !hasValue(value)
        ? field.required
          ? "missing"
          : "not_applicable"
        : String(value) === String(original)
          ? "confirmed"
          : "corrected",
    };
  });
}

function hasValue(value) {
  return (
    value !== null
    && value !== undefined
    && String(value).trim() !== ""
  );
}

function confidenceLabel(confidence) {
  const percentage = Math.round((confidence || 0) * 100);
  if (percentage >= 85) return `${percentage}% · alta`;
  if (percentage >= 60) return `${percentage}% · media`;
  return `${percentage}% · revisar`;
}

function FieldInput({ disabled = false, field, value, onChange }) {
  if (field.type === "boolean") {
    return (
      <select disabled={disabled} value={String(value)} onChange={(event) => onChange(event.target.value)}>
        <option value="">Sin completar</option>
        <option value="true">Sí</option>
        <option value="false">No</option>
      </select>
    );
  }
  const copyMetadata = field.type !== "date"
    ? copyableFieldMetadata(field)
    : null;
  const copyValue = copyMetadata?.copyFormat === "money"
    ? formatMoneyForCopy(value)
    : value;
  return (
    <span className={`review-copyable-input ${copyMetadata && hasValue(value) ? "has-copy-action" : ""}`}>
      <input
        type={field.type === "date" ? "date" : "text"}
        inputMode={["number", "currency"].includes(field.type) ? "decimal" : undefined}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        readOnly={field.sourceMode === "computed"}
        disabled={disabled}
        placeholder="Sin información"
      />
      {copyMetadata && hasValue(copyValue) && (
        <CopyableText
          className="review-copy-value-action"
          copiedMessage={copyMetadata.copiedMessage}
          iconOnly
          label={copyMetadata.label}
          value={copyValue}
        />
      )}
    </span>
  );
}

function documentEvidence(field) {
  return field.evidence?.find(
    (evidence) => evidence.sourceType !== "external" && evidence.documentId,
  );
}

function ReviewFieldRow({
  field,
  value,
  active,
  onChange,
  disabled = false,
  onSelect,
  onSelectEvidence,
}) {
  const optionalWithoutValue = !field.required && !hasValue(value);
  const fieldDocumentEvidence = documentEvidence(field);
  const externalEvidence = field.evidence?.find(
    (evidence) => evidence.sourceType === "external" && evidence.sourceUrl,
  );
  const selectField = (event) => {
    if (!fieldDocumentEvidence) return;
    if (event.target.closest("input, select, button, a")) return;
    onSelect(fieldDocumentEvidence, field.key);
  };

  return (
    <article
      className={`review-field-row${
        optionalWithoutValue ? " is-optional-empty" : ""
      }${active ? " is-active" : ""}${
        fieldDocumentEvidence ? " is-selectable" : ""
      }`}
      onClick={selectField}
    >
      <div className="review-field-name">
        <strong>{field.label}</strong>
        <small className={field.required && !hasValue(value)
          ? "required-warning"
          : ""}
        >
          {field.required && !hasValue(value)
            ? "Obligatorio sin completar"
            : field.target?.sheet
              ? `${field.target.sheet}!${field.target.cell}`
              : field.sourceMode === "manual"
                ? "Carga manual"
                : "Sin destino"}
        </small>
      </div>
      <div className="review-field-input">
        <FieldInput disabled={disabled} field={field} value={value} onChange={onChange} />
      </div>
      {["extracted", "computed"].includes(field.sourceMode) ? (
        <span className={`confidence ${
          optionalWithoutValue
            ? "confidence-neutral"
            : `confidence-${
              field.confidence >= 0.85
                ? "high"
                : field.confidence >= 0.6
                  ? "medium"
                  : "low"
            }`
        }`}>
          {optionalWithoutValue
            ? "Sin dato"
            : field.sourceMode === "computed"
              ? "Calculado"
              : confidenceLabel(field.confidence)}
        </span>
      ) : <span className="confidence confidence-neutral">Manual</span>}
      {fieldDocumentEvidence ? (
        <button
          type="button"
          className="review-evidence-button"
          onClick={() => onSelectEvidence(fieldDocumentEvidence, field.key)}
          title={`${fieldDocumentEvidence.fileName} · pág. ${fieldDocumentEvidence.page}`}
          aria-label={`Ver evidencia de ${field.label}`}
        >
          <FileSearch size={16} />
          <span>Pág. {fieldDocumentEvidence.page}</span>
        </button>
      ) : externalEvidence ? (
        <a
          className="review-evidence-button"
          href={externalEvidence.sourceUrl}
          target="_blank"
          rel="noreferrer"
          title={externalEvidence.sourceLabel || "Fuente externa"}
        >
          <ExternalLink size={15} /> <span>Fuente</span>
        </a>
      ) : <span className="review-no-evidence">—</span>}
    </article>
  );
}

function ReviewRangeGroup({
  fields,
  values,
  activeFieldKey,
  onChange,
  disabled = false,
  onSelectEvidence,
}) {
  const ordered = [...fields].sort((left, right) => (
    left.range.rowIndex - right.range.rowIndex
    || left.range.columnIndex - right.range.columnIndex
  ));
  const first = ordered[0];
  const columns = [...new Map(ordered.map((field) => [
    field.range.columnIndex,
    {
      index: field.range.columnIndex,
      label: field.range.columnLabel,
    },
  ])).values()].sort((left, right) => left.index - right.index);
  const rows = [...new Set(ordered.map((field) => field.range.rowIndex))];
  const byCell = new Map(ordered.map((field) => [
    `${field.range.rowIndex}:${field.range.columnIndex}`,
    field,
  ]));

  return (
    <section className="review-range-group">
      <header>
        <div>
          <Rows3 size={16} />
          <strong>{first.range.groupLabel}</strong>
        </div>
        <small>{rows.length} filas · {columns.length} columnas</small>
      </header>
      <div className="review-range-scroll">
        <table>
          <thead>
            <tr>
              <th>Fila</th>
              {columns.map((column) => (
                <th key={column.index}>{column.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((rowIndex) => (
              <tr key={rowIndex}>
                <th>{rowIndex + 1}</th>
                {columns.map((column) => {
                  const field = byCell.get(`${rowIndex}:${column.index}`);
                  if (!field) return <td key={column.index} />;
                  const evidence = documentEvidence(field);
                  const value = values[field.key] ?? "";
                  const missing = field.required && !hasValue(value);
                  return (
                    <td
                      key={column.index}
                      className={`${activeFieldKey === field.key ? "is-active" : ""}${missing ? " is-missing" : ""}`}
                    >
                      <div className="review-range-cell">
                        <FieldInput
                          disabled={disabled}
                          field={field}
                          value={value}
                          onChange={(nextValue) => onChange(field.key, nextValue)}
                        />
                        {evidence && (
                          <button
                            type="button"
                            onClick={() => onSelectEvidence(evidence, field.key)}
                            title={`${evidence.fileName} · pág. ${evidence.page}`}
                            aria-label={`Ver evidencia de ${field.label}`}
                          >
                            <FileSearch size={14} />
                          </button>
                        )}
                      </div>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function ReviewBlockGroup({
  fields,
  values,
  activeFieldKey,
  onChange,
  disabled = false,
  onSelectEvidence,
}) {
  const ordered = [...fields].sort((left, right) => (
    left.block.instanceIndex - right.block.instanceIndex
    || left.block.attributeIndex - right.block.attributeIndex
  ));
  const first = ordered[0];
  const attributes = [...new Map(ordered.map((field) => [
    field.block.attributeIndex,
    {
      index: field.block.attributeIndex,
      label: field.block.attributeLabel,
    },
  ])).values()].sort((left, right) => left.index - right.index);
  const instances = [...new Map(ordered.map((field) => [
    field.block.instanceIndex,
    {
      index: field.block.instanceIndex,
      key: field.block.instanceKey,
      label: field.block.instanceLabel,
      targetSheet: field.block.targetSheet,
      targetCell: field.block.targetCell,
    },
  ])).values()].sort((left, right) => left.index - right.index);
  const byCell = new Map(ordered.map((field) => [
    `${field.block.instanceIndex}:${field.block.attributeIndex}`,
    field,
  ]));

  return (
    <section className="review-range-group review-block-group">
      <header>
        <div>
          <PanelsTopLeft size={16} />
          <strong>{first.block.groupLabel}</strong>
        </div>
        <small>{instances.length} instancias · {attributes.length} datos</small>
      </header>
      <div className="review-range-scroll">
        <table>
          <thead>
            <tr>
              <th>Instancia</th>
              {attributes.map((attribute) => (
                <th key={attribute.index}>{attribute.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {instances.map((instance) => (
              <tr key={instance.key}>
                <th className="review-block-instance">
                  <strong>{instance.label}</strong>
                  {instance.targetSheet && (
                    <small>{instance.targetSheet}!{instance.targetCell}</small>
                  )}
                </th>
                {attributes.map((attribute) => {
                  const field = byCell.get(`${instance.index}:${attribute.index}`);
                  if (!field) return <td key={attribute.index} />;
                  const evidence = documentEvidence(field);
                  const value = values[field.key] ?? "";
                  const missing = field.required && !hasValue(value);
                  return (
                    <td
                      key={attribute.index}
                      className={`${activeFieldKey === field.key ? "is-active" : ""}${missing ? " is-missing" : ""}`}
                    >
                      <div className="review-range-cell">
                        <FieldInput
                          disabled={disabled}
                          field={field}
                          value={value}
                          onChange={(nextValue) => onChange(field.key, nextValue)}
                        />
                        {evidence && (
                          <button
                            type="button"
                            onClick={() => onSelectEvidence(evidence, field.key)}
                            title={`${evidence.fileName} · pág. ${evidence.page}`}
                            aria-label={`Ver evidencia de ${field.label}`}
                          >
                            <FileSearch size={14} />
                          </button>
                        )}
                      </div>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

export function RunDetailPage({ runId }) {
  const api = useApi();
  const { navigate } = useRouter();
  const reviewWorkspaceRef = useRef(null);
  const currentRunIdRef = useRef(runId);
  const lastSavedCorrectionsRef = useRef(null);
  const draftSaveChainRef = useRef(Promise.resolve());
  const [run, setRun] = useState(null);
  const [values, setValues] = useState({});
  const [activeEvidence, setActiveEvidence] = useState(null);
  const [activeFieldKey, setActiveFieldKey] = useState(null);
  const [reviewSplit, setReviewSplit] = useState(initialReviewSplit);
  const [mobileReviewPanel, setMobileReviewPanel] = useState("fields");
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [pendingDraft, setPendingDraft] = useState(null);
  const [state, setState] = useState({
    loading: true,
    saving: false,
    generating: false,
    approving: false,
    deleting: false,
    error: "",
    saved: false,
  });

  useEffect(() => {
    currentRunIdRef.current = runId;
    lastSavedCorrectionsRef.current = null;
    draftSaveChainRef.current = Promise.resolve();
    setRun(null);
    setValues({});
    setPendingDraft(null);
    setActiveEvidence(null);
    setActiveFieldKey(null);
    setMobileReviewPanel("fields");
    setState({
      loading: true,
      saving: false,
      generating: false,
      approving: false,
      deleting: false,
      error: "",
      saved: false,
    });
  }, [runId]);

  useEffect(() => {
    let active = true;
    let timer;
    const load = async () => {
      try {
        const nextRun = await api.getRun(runId);
        if (!active) return;
        setRun(nextRun);
        setValues((current) => {
          if (Object.keys(current).length) return current;
          const nextValues = Object.fromEntries(
            (nextRun.fields || []).map((field) => [field.key, displayValue(field)]),
          );
          lastSavedCorrectionsRef.current = JSON.stringify(
            correctionsForRun(nextRun, nextValues),
          );
          return nextValues;
        });
        setState((current) => ({ ...current, loading: false, error: "" }));
        if (processingStatuses.has(nextRun.status)) {
          timer = window.setTimeout(load, 1800);
        }
      } catch (error) {
        if (active) {
          setState((current) => ({
            ...current,
            loading: false,
            error: error.message,
          }));
        }
      }
    };
    load();
    return () => {
      active = false;
      window.clearTimeout(timer);
    };
  }, [api, runId]);

  useEffect(() => {
    window.localStorage.setItem(
      REVIEW_SPLIT_STORAGE_KEY,
      String(reviewSplit),
    );
  }, [reviewSplit]);

  const corrections = useMemo(
    () => correctionsForRun(run, values),
    [run, values],
  );

  useEffect(() => {
    if (
      !run?.fields?.length
      || !["awaiting_review", "completed"].includes(run.status)
      || run.governance?.state === "approved"
      || state.generating
    ) return;
    const serialized = JSON.stringify(corrections);
    if (serialized === lastSavedCorrectionsRef.current) return;
    setPendingDraft({ corrections, serialized });
    setState((current) => ({ ...current, saved: false }));
  }, [corrections, run, state.generating]);

  useEffect(() => {
    if (!pendingDraft) return undefined;
    const timer = window.setTimeout(() => {
      persistDraft(pendingDraft).catch(() => {});
    }, 650);
    return () => window.clearTimeout(timer);
  }, [pendingDraft]);
  const missingRequired = useMemo(
    () => (run?.fields || []).filter(
      (field) => field.required && String(values[field.key] ?? "").trim() === "",
    ).length,
    [run, values],
  );
  const visibleFields = useMemo(
    () => (run?.fields || []).filter(
      (field) => !field.range?.groupKey && !field.block?.groupKey && (
        field.required || hasValue(displayValue(field))
      ),
    ),
    [run],
  );
  const optionalEmptyFields = useMemo(
    () => (run?.fields || []).filter(
      (field) => (
        !field.range?.groupKey
        && !field.block?.groupKey
        && !field.required
        && !hasValue(displayValue(field))
      ),
    ),
    [run],
  );
  const rangeGroups = useMemo(() => {
    const groups = new Map();
    for (const field of run?.fields || []) {
      const key = field.range?.groupKey;
      if (!key) continue;
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(field);
    }
    return [...groups.values()];
  }, [run]);
  const blockGroups = useMemo(() => {
    const groups = new Map();
    for (const field of run?.fields || []) {
      const key = field.block?.groupKey;
      if (!key) continue;
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(field);
    }
    return [...groups.values()];
  }, [run]);
  const reviewableFields = useMemo(
    () => [...visibleFields, ...rangeGroups.flat(), ...blockGroups.flat()],
    [blockGroups, rangeGroups, visibleFields],
  );

  useEffect(() => {
    if (!run || activeEvidence || !["awaiting_review", "completed"].includes(
      run.status,
    )) {
      return;
    }
    const firstField = reviewableFields.find((field) =>
      field.evidence?.some(
        (evidence) =>
          evidence.sourceType !== "external" && evidence.documentId,
      ));
    const evidence = firstField?.evidence.find(
      (candidate) =>
        candidate.sourceType !== "external" && candidate.documentId,
    );
    if (evidence) {
      setActiveEvidence(evidence);
      setActiveFieldKey(firstField.key);
      return;
    }
    const document = run.documents?.[0];
    if (document) {
      setActiveEvidence({
        documentId: document.id,
        fileName: document.fileName,
        page: 1,
        quote: null,
        boxes: [],
      });
    }
  }, [activeEvidence, reviewableFields, run]);

  const selectEvidence = (evidence, fieldKey = null) => {
    setActiveEvidence(evidence);
    setActiveFieldKey(fieldKey);
    if (window.matchMedia("(max-width: 900px)").matches) {
      setMobileReviewPanel("document");
    }
  };

  const selectDocument = (document) => {
    selectEvidence({
      documentId: document.id,
      fileName: document.fileName,
      page: 1,
      quote: null,
      boxes: [],
    });
    setActiveFieldKey(null);
  };

  const startReviewResize = (event) => {
    const workspace = reviewWorkspaceRef.current;
    if (!workspace) return;
    event.preventDefault();
    const bounds = workspace.getBoundingClientRect();
    const minimumLeft = Math.min(390, bounds.width * 0.46);
    const minimumRight = Math.min(430, bounds.width * 0.5);
    const onPointerMove = (moveEvent) => {
      const minimum = minimumLeft / bounds.width;
      const maximum = 1 - minimumRight / bounds.width;
      setReviewSplit(clamp(
        (moveEvent.clientX - bounds.left) / bounds.width,
        minimum,
        Math.max(minimum, maximum),
      ));
    };
    const onPointerUp = () => {
      document.removeEventListener("pointermove", onPointerMove);
      document.removeEventListener("pointerup", onPointerUp);
      document.body.classList.remove("is-resizing-panels");
    };
    document.body.classList.add("is-resizing-panels");
    document.addEventListener("pointermove", onPointerMove);
    document.addEventListener("pointerup", onPointerUp);
  };

  const persistDraft = (draft = pendingDraft) => {
    if (!run?.fields?.length || !draft) return Promise.resolve(run);
    setState((current) => ({
      ...current,
      saving: true,
      saved: false,
      error: "",
    }));
    const operation = draftSaveChainRef.current
      .catch(() => {})
      .then(() => api.saveRunDraft(runId, draft.corrections))
      .then((nextRun) => {
        if (currentRunIdRef.current !== runId) return nextRun;
        lastSavedCorrectionsRef.current = draft.serialized;
        setPendingDraft((current) => (
          current?.serialized === draft.serialized ? null : current
        ));
        setRun(nextRun);
        setState((current) => ({
          ...current,
          saving: false,
          saved: true,
          error: "",
        }));
        return nextRun;
      })
      .catch((error) => {
        if (currentRunIdRef.current === runId) {
          setState((current) => ({
            ...current,
            saving: false,
            saved: false,
            error: error.message,
          }));
        }
        throw error;
      });
    draftSaveChainRef.current = operation;
    return operation;
  };

  const leaveToHistory = async () => {
    try {
      if (pendingDraft) await persistDraft(pendingDraft);
      await draftSaveChainRef.current;
      navigate("/procesador-documental");
    } catch {
      // The error banner explains why navigation was stopped.
    }
  };

  const save = async () => {
    if (!run?.fields?.length || state.saving) return null;
    setState((current) => ({ ...current, saving: true, saved: false, error: "" }));
    try {
      if (pendingDraft) await persistDraft(pendingDraft);
      await draftSaveChainRef.current;
      const nextRun = await api.reviewRun(runId, corrections);
      lastSavedCorrectionsRef.current = JSON.stringify(corrections);
      setPendingDraft(null);
      setRun(nextRun);
      setValues(Object.fromEntries(
        (nextRun.fields || []).map((field) => [field.key, displayValue(field)]),
      ));
      setState((current) => ({ ...current, saving: false, saved: true }));
      return nextRun;
    } catch (error) {
      setState((current) => ({
        ...current,
        saving: false,
        error: error.message,
      }));
      return null;
    }
  };

  const generate = async () => {
    if (state.generating) return;
    setState((current) => ({
      ...current,
      generating: true,
      saved: false,
      error: "",
    }));
    try {
      const reviewed = await save();
      if (!reviewed) {
        setState((current) => ({ ...current, generating: false }));
        return;
      }
      const nextRun = await api.generateRun(runId);
      setRun(nextRun);
      setState((current) => ({
        ...current,
        saving: false,
        generating: false,
        saved: true,
      }));
    } catch (error) {
      setState((current) => ({
        ...current,
        saving: false,
        generating: false,
        error: error.message,
      }));
    }
  };

  const deleteRun = async () => {
    if (state.deleting) return;
    setState((current) => ({ ...current, deleting: true, error: "" }));
    try {
      await api.deleteRun(runId);
      navigate("/procesador-documental");
    } catch (error) {
      setConfirmDelete(false);
      setState((current) => ({
        ...current,
        deleting: false,
        error: error.message,
      }));
    }
  };

  const approve = async () => {
    if (state.approving) return;
    setState((current) => ({ ...current, approving: true, error: "" }));
    try {
      const nextRun = await api.approveRun(runId);
      setRun(nextRun);
      setState((current) => ({ ...current, approving: false, saved: true }));
    } catch (error) {
      setState((current) => ({
        ...current,
        approving: false,
        error: error.message,
      }));
    }
  };

  if (state.loading || !run) {
    return (
      <div className="page run-detail-page">
        <LoadingBlock>Abriendo el procesamiento…</LoadingBlock>
      </div>
    );
  }

  const [statusTitle, statusDescription] = statusCopy[run.status] || statusCopy.queued;
  const processing = processingStatuses.has(run.status);
  const reviewable = ["awaiting_review", "completed"].includes(run.status);
  const approved = run.governance?.state === "approved";

  return (
    <div className="page run-detail-page">
      <button
        className="back-link"
        onClick={leaveToHistory}
      >
        <ArrowLeft size={16} /> Volver
      </button>

      <PageHeader
        className="run-header"
        eyebrow="Procesamiento"
        title={run.template?.name || "Resultado"}
        description={`${run.inputCount} PDF${
          run.inputCount === 1 ? "" : "s"
        } · ${run.totalPages ?? "—"} páginas`}
        actions={(
          <>
            {run.permissions?.canArchive && <button
              className="danger-button"
              onClick={() => setConfirmDelete(true)}
              disabled={processingStatuses.has(run.status)}
              title={processingStatuses.has(run.status)
                ? "Podrás eliminarlo cuando termine el procesamiento"
                : undefined}
            >
              <Trash2 size={17} /> {run.workspaceType === "organization" ? "Archivar" : "Eliminar"}
            </button>}
            {run.governance?.report.available && (
              <button
                className="secondary-button"
                title={run.governance.report.sha256
                  ? `SHA-256 ${run.governance.report.sha256}`
                  : undefined}
                onClick={() => api.downloadRunReport(run.id)}
              >
                <Download size={18} /> Reporte de la organización
              </button>
            )}
            {run.hasOutput && (
              <button
                className="primary-button"
                onClick={() => api.downloadOutput(run.id)}
              >
                <Download size={18} /> Descargar Excel
              </button>
            )}
          </>
        )}
      />

      {state.error && <FeedbackBanner>{state.error}</FeedbackBanner>}

      {run.version > 0 && reviewable && !approved && (
        <FeedbackBanner tone="warning">
          {run.draftDirty
            ? `Estás editando la versión ${run.version}. Al finalizar, se guardará como versión ${run.version + 1} y se conservará la anterior.`
            : `Estás viendo la versión ${run.version}. Si modificás sus valores y volvés a finalizar, conservaremos esta versión.`}
        </FeedbackBanner>
      )}

      {run.workspaceType === "organization" && (
        <section className="institutional-governance-strip">
          <ShieldCheck size={18} />
          <span>
            <strong>{approved ? "Resultado aprobado e inmutable" : run.governance?.state === "reviewed" ? "Resultado revisado" : "Pendiente de revisión"}</strong>
            <small>
              Operador {run.createdByName || "Usuario"}
              {run.governance?.reviewedBy
                ? ` · Revisor ${run.governance.reviewedByName || "Usuario"}`
                : ""}
              {run.governance?.approvedBy
                ? ` · Aprobador ${run.governance.approvedByName || "Usuario"}`
                : ""}
              {run.governance?.report.sha256 ? ` · SHA-256 ${run.governance.report.sha256.slice(0, 12)}…` : ""}
            </small>
          </span>
        </section>
      )}

      <section className={`run-status status-panel-${run.status}`}>
        <span className="status-visual">
          {processing
            ? <LoaderCircle className="spin" size={25} />
            : run.status === "failed"
              ? <AlertTriangle size={25} />
              : <CheckCircle2 size={25} />}
        </span>
        <div>
          <strong>{statusTitle}</strong>
          <p>{run.error?.message || statusDescription}</p>
        </div>
        {processing && <span className="status-live">EN PROCESO</span>}
      </section>

      {run.warnings?.length > 0 && (
        <FeedbackBanner tone="warning">
          <div>
            <strong>Hay información que requiere atención</strong>
            {run.warnings.map((warning) => <p key={warning}>{warning}</p>)}
          </div>
        </FeedbackBanner>
      )}

      {!reviewable && (
        <section className="section-block documents-section">
          <SectionHeader
            eyebrow="Fuentes"
            title="Documentos procesados"
          />
          <div className="document-grid">
            {run.documents.map((document) => (
              <button
                className="document-card"
                key={document.id}
                onClick={() => api.openDocument(run.id, document.id)}
              >
                <FileText size={20} />
                <span>
                  <strong>{document.fileName}</strong>
                  <small>
                    {document.pageCount ?? "—"} páginas ·{" "}
                    {["text_ready", "ocr_ready", "ocr_required"].includes(
                      document.extractionStatus,
                    )
                      ? document.extractionStatus === "ocr_ready"
                        ? "Texto leído con OCR"
                        : document.extractionStatus === "ocr_required"
                          ? "Lectura parcial"
                          : "Texto leído"
                      : "Procesando"}
                  </small>
                </span>
                <ExternalLink size={16} />
              </button>
            ))}
          </div>
        </section>
      )}

      {reviewable && (
        <section className="section-block review-section">
          <div className="review-mobile-switch" role="tablist">
            <button
              type="button"
              role="tab"
              aria-selected={mobileReviewPanel === "fields"}
              className={mobileReviewPanel === "fields" ? "is-active" : ""}
              onClick={() => setMobileReviewPanel("fields")}
            >
              Campos
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={mobileReviewPanel === "document"}
              className={mobileReviewPanel === "document" ? "is-active" : ""}
              onClick={() => setMobileReviewPanel("document")}
            >
              Documento
            </button>
          </div>

          <div
            className={`review-workspace mobile-${mobileReviewPanel}`}
            ref={reviewWorkspaceRef}
            style={{
              gridTemplateColumns: `${reviewSplit * 100}% 12px minmax(0, 1fr)`,
            }}
          >
            <div className="review-fields-panel">
              <SectionHeader
                eyebrow="Revisión humana"
                title="Verificá los valores"
                description="Seleccioná un campo para ver su fuente en el documento."
                actions={(
                  <span className="review-count">
                    {visibleFields.length + rangeGroups.length + blockGroups.length} configuraciones
                  </span>
                )}
              />

              <div className="review-table">
                <div className="review-table-header" aria-hidden="true">
                  <span>Campo</span><span>Valor</span><span>Estado</span><span>Fuente</span>
                </div>
                {visibleFields.map((field) => (
                  <ReviewFieldRow
                    key={field.key}
                    field={field}
                    value={values[field.key] ?? ""}
                    active={activeFieldKey === field.key}
                    onChange={(value) => {
                      setValues((current) => ({
                        ...current,
                        [field.key]: value,
                      }));
                      setState((current) => ({
                        ...current,
                        saved: false,
                      }));
                    }}
                    onSelect={selectEvidence}
                    onSelectEvidence={selectEvidence}
                    disabled={approved}
                  />
                ))}
              </div>

              {rangeGroups.length > 0 && (
                <div className="review-range-list">
                  {rangeGroups.map((fields) => (
                    <ReviewRangeGroup
                      key={fields[0].range.groupKey}
                      fields={fields}
                      values={values}
                      activeFieldKey={activeFieldKey}
                      onChange={(key, value) => {
                        setValues((current) => ({ ...current, [key]: value }));
                        setState((current) => ({ ...current, saved: false }));
                      }}
                      onSelectEvidence={selectEvidence}
                      disabled={approved}
                    />
                  ))}
                </div>
              )}

              {blockGroups.length > 0 && (
                <div className="review-range-list review-block-list">
                  {blockGroups.map((fields) => (
                    <ReviewBlockGroup
                      key={fields[0].block.groupKey}
                      fields={fields}
                      values={values}
                      activeFieldKey={activeFieldKey}
                      onChange={(key, value) => {
                        setValues((current) => ({ ...current, [key]: value }));
                        setState((current) => ({ ...current, saved: false }));
                      }}
                      onSelectEvidence={selectEvidence}
                      disabled={approved}
                    />
                  ))}
                </div>
              )}

              {optionalEmptyFields.length > 0 && (
                <details className="optional-fields">
                  <summary>
                    <span>
                      <strong>Campos opcionales sin información</strong>
                      <small>
                        {optionalEmptyFields.length} campo{
                          optionalEmptyFields.length === 1 ? "" : "s"
                        }
                      </small>
                    </span>
                    <ChevronDown size={18} />
                  </summary>
                  <div className="review-table optional-review-table">
                    {optionalEmptyFields.map((field) => (
                      <ReviewFieldRow
                        key={field.key}
                        field={field}
                        value={values[field.key] ?? ""}
                        active={activeFieldKey === field.key}
                        onChange={(value) => {
                          setValues((current) => ({
                            ...current,
                            [field.key]: value,
                          }));
                          setState((current) => ({
                            ...current,
                            saved: false,
                          }));
                        }}
                        onSelect={selectEvidence}
                        onSelectEvidence={selectEvidence}
                        disabled={approved}
                      />
                    ))}
                  </div>
                </details>
              )}

              {!approved && <div className="sticky-actions review-actions">
                <span className={
                  state.saving || state.saved || missingRequired > 0
                    ? "save-confirmation visible"
                    : "save-confirmation"
                }>
                  {missingRequired > 0
                    ? `${missingRequired} obligatorio${
                      missingRequired === 1 ? "" : "s"
                    } pendiente${missingRequired === 1 ? "" : "s"}`
                    : state.saving
                      ? "Guardando cambios…"
                      : <><Check size={15} /> Cambios guardados</>}
                </span>
                <div className="review-action-buttons">
                  <button
                    className="secondary-button"
                    disabled={state.saving || state.generating}
                    onClick={save}
                  >
                    <Save size={17} />
                    {state.saving && !state.generating
                      ? "Guardando…"
                      : "Guardar"}
                  </button>
                  {run.hasOutput && run.outputKind === "excel" && (
                    <button
                      className="secondary-button download-button"
                      disabled={state.saving || state.generating}
                      onClick={() => api.downloadOutput(run.id)}
                    >
                      <Download size={18} /> Descargar
                    </button>
                  )}
                  <button
                    className="primary-button"
                    disabled={
                      state.saving || state.generating || missingRequired > 0
                    }
                    onClick={generate}
                  >
                    {state.generating
                      ? <LoaderCircle className="spin" size={18} />
                      : run.hasOutput
                        ? <RefreshCw size={18} />
                        : <Sparkles size={18} />}
                    {state.generating
                      ? "Generando…"
                      : run.outputKind === "display"
                        ? run.status === "completed"
                          ? "Guardar cambios"
                          : "Finalizar"
                        : run.hasOutput
                          ? "Regenerar"
                          : "Generar"}
                  </button>
                  {run.permissions?.canApprove && (
                    <button
                      className="primary-button"
                      disabled={state.approving}
                      onClick={approve}
                    >
                      {state.approving
                        ? <LoaderCircle className="spin" size={18} />
                        : <ShieldCheck size={18} />}
                      {state.approving ? "Aprobando…" : "Aprobar y emitir reporte"}
                    </button>
                  )}
                </div>
              </div>}
            </div>

            <button
              className="review-resizer"
              type="button"
              aria-label="Ajustar ancho de los paneles"
              onPointerDown={startReviewResize}
            />

            <div className="review-preview-panel">
              {activeEvidence && (
                <EvidenceViewer
                  embedded
                  runId={run.id}
                  evidence={activeEvidence}
                  documents={run.documents}
                  onSelectDocument={selectDocument}
                />
              )}
            </div>
          </div>
        </section>
      )}
      {confirmDelete && (
        <ConfirmDialog
          title={run.workspaceType === "organization" ? "Archivar procesamiento" : "Eliminar procesamiento"}
          description={run.workspaceType === "organization"
            ? "Dejará de aparecer en el historial activo, pero se conservará su trazabilidad."
            : "Se eliminarán este registro, sus PDFs y el resultado generado. Esta acción no se puede deshacer."}
          confirmLabel={state.deleting
            ? "Guardando…"
            : run.workspaceType === "organization"
              ? "Archivar procesamiento"
              : "Eliminar procesamiento"}
          onConfirm={deleteRun}
          onClose={() => {
            if (!state.deleting) setConfirmDelete(false);
          }}
          busy={state.deleting}
        />
      )}
    </div>
  );
}
